// ======================= Imports =======================
import axios from "axios";
import { RefreshAccessToken } from "./RefreshAccessToken";
import { toast } from "react-toastify";

// ======================= Axios Instance =======================
const api = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

// ======================= Token Refresh State =======================
let isTokenRefreshing = false;
let failedQueue = [];

// ======================= Request Interceptor =======================
api.interceptors.request.use(
  (config) => {
    const userData = localStorage.getItem("userData");
    const token = userData ? JSON.parse(userData).token : null;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    console.error("Request error:", error.message);
    return Promise.reject(error);
  },
);

// ======================= Response Interceptor =======================
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const { config, response } = error;

    // ======================= Handle Invalid Session =======================
    const sessionInvalidMessages = [
      "Session invalid. Please log in again.",
      "Unauthorized",
      "Session revoked. Please log in again.",
    ];
    const serverMessage = response?.data?.message;
    if (
      response &&
      response.status === 401 &&
      sessionInvalidMessages.includes(serverMessage)
    ) {
      toast.error(serverMessage);
      localStorage.clear();
      window.location.href = "/login";
    }
    // ======================= Handle Token Expiry & Refresh =======================
    else if (response && response.status === 401) {
      const originalRequest = config;
      if (isTokenRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, originalRequest });
        });
      }

      isTokenRefreshing = true;

      try {
        const refreshedToken = await RefreshAccessToken();
        originalRequest.headers["Authorization"] = `Bearer ${refreshedToken}`;
        const retryResponse = await axios(originalRequest);
        failedQueue.forEach(({ resolve }) => resolve(retryResponse));
        failedQueue = [];
        return retryResponse;
      } catch (err) {
        failedQueue.forEach(({ reject }) => reject(err));
        failedQueue = [];
        toast.error(err);
        return Promise.reject(err);
      } finally {
        isTokenRefreshing = false;
      }
    }

    // ======================= Handle Server Error =======================
    if (response && response.status === 500) {
      console.error("Server error - Try again later");
    }

    return Promise.reject(error);
  },
);

// ======================= Export API =======================
export { api };
